/**
 * ByokKeysSection — list + add + delete UI for the user's stored AI keys.
 *
 * Lives under Settings → General → Theme & Appearance. Keys are per-user
 * (not per-tenant) in this phase; tenant-shared keys are a follow-up.
 */
import * as React from "react";
import { formatDistanceToNow } from "date-fns";
import { KeyRound, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import {
  GlassCard,
  GlassCardContent,
  GlassCardDescription,
  GlassCardHeader,
  GlassCardTitle,
} from "@/components/ui/glass-card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

import { ByokAddKeyDialog } from "./ByokAddKeyDialog";
import {
  useByokKeys,
  useDeleteByokKey,
  type ByokKeyRow,
} from "@/theme/byok/useByokKeys";
import { getProviderInfo } from "@/theme/byok/providers";

export function ByokKeysSection() {
  const { data: keys = [], isLoading } = useByokKeys();
  const del = useDeleteByokKey();
  const [addOpen, setAddOpen] = React.useState(false);
  const [pendingDelete, setPendingDelete] = React.useState<ByokKeyRow | null>(null);

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    try {
      await del.mutateAsync(pendingDelete.id);
      toast.success("API key deleted");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete API key");
    } finally {
      setPendingDelete(null);
    }
  };

  return (
    <GlassCard variant="default" hover={false}>
      <GlassCardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <GlassCardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            AI provider keys
          </GlassCardTitle>
          <GlassCardDescription>
            Bring your own keys for theme generation. Keys are encrypted at
            rest and decrypted only inside the server function — never sent
            back to the browser.
          </GlassCardDescription>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus className="mr-1 h-4 w-4" />
          Add key
        </Button>
      </GlassCardHeader>
      <GlassCardContent>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : keys.length === 0 ? (
          <div className="rounded-lg border border-dashed border-border bg-muted/40 p-6 text-center">
            <p className="text-sm text-muted-foreground">
              No personal keys yet. Theme generation will use the included
              Lovable AI gateway by default. Add your own key to use OpenAI,
              Anthropic, Gemini, Mistral, Groq, or OpenRouter.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border rounded-lg border border-border">
            {keys.map((k) => {
              const info = getProviderInfo(k.provider);
              return (
                <li
                  key={k.id}
                  className="flex items-center justify-between gap-3 p-3"
                >
                  <div className="flex items-center gap-3">
                    <Badge variant="outline">{info.label}</Badge>
                    <div className="text-sm">
                      <p className="font-medium">{k.label}</p>
                      <p className="text-xs text-muted-foreground">
                        Added{" "}
                        {formatDistanceToNow(new Date(k.created_at), {
                          addSuffix: true,
                        })}
                        {k.last_used_at
                          ? ` · last used ${formatDistanceToNow(new Date(k.last_used_at), {
                              addSuffix: true,
                            })}`
                          : " · never used"}
                      </p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete API key ${k.label}`}
                    onClick={() => setPendingDelete(k)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </GlassCardContent>

      <ByokAddKeyDialog open={addOpen} onOpenChange={setAddOpen} />

      <AlertDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => !open && setPendingDelete(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete API key</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{pendingDelete?.label}"? This
              action cannot be undone. The encrypted key will be removed from
              the vault and any saved themes that reference it will fall back
              to the Lovable AI gateway.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={del.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {del.isPending ? "Deleting…" : "Delete key"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </GlassCard>
  );
}
